import { BlankSlate } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { ContentCard } from "@msuite/ui/content-card";
import { Draggable } from "@msuite/ui/dnd";
import { TextInput } from "@msuite/ui/form";
import { useFilter, useSearchForm } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { guardArray, uniqueArray } from "@msuite/utils/array";
import { useMemo } from "react";
import { VehicleCard } from "@/components/cards/vehicle-card";
import { useDaily } from "@/components/plan/daily/use-daily";
import { usePlanStore } from "@/components/plan/store";
import { useVehicles } from "@/hooks/use-vehicles";

export function VehicleResources() {
	/** Store */
	const activeDate = usePlanStore((state) => state.activeDate);

	/** Hooks */
	const { t } = useTranslation();
	const { ownCrews, plan } = useDaily(activeDate.db());
	const searchForm = useSearchForm("vehicle-resources");
	const { vehicles, isFetching, refetch } = useVehicles();

	/** Memoized */
	const usedVehicles = useMemo(() => {
		return uniqueArray(
			guardArray(ownCrews.map((crew) => crew.vehicleAssignment?.vehicleId) as string[]).concat(
				guardArray(plan?.vehicleAssignmentsPlan.map((vap) => vap.vehicleId)) as string[],
			),
		);
	}, [ownCrews, plan]);

	const availableVehicles = useMemo(() => {
		return vehicles.filter((vehicle) => !usedVehicles.includes(vehicle.id));
	}, [usedVehicles, vehicles]);

	const { filtered } = useFilter(availableVehicles, searchForm.searchValue);

	/** Render */
	return (
		<ContentCard
			title={t("vehicles.vehicles")}
			icon={icon("User")}
			contentClassName="v-stack flex-1 min-h-0 overflow-y-auto"
			className="h-full overflow-hidden"
			variant="plain"
		>
			<div className="h-stack">
				<TextInput {...searchForm.getSearchInputProps()} />
				<Button
					icon={<Icon name="RefreshCcw" />}
					size="sm"
					onClick={refetch}
					isLoading={isFetching}
				/>
			</div>
			{!filtered.length && <BlankSlate withBorder />}
			{filtered?.map((vehicle) => (
				<Draggable
					key={vehicle?.id}
					item={vehicle}
					type="vehicle"
				>
					<VehicleCard
						vehicle={vehicle}
						vehicleId={vehicle.id}
					/>
				</Draggable>
			))}
		</ContentCard>
	);
}
